import React, { useState } from 'react';
import posthog from 'posthog-js';
import { Icon } from './ui/Icon.jsx';
import { LangToggle } from './ui/LangToggle.jsx';
import { supaFetch } from '../lib/supabase.js';
import { COLORS, FONT, input, button } from '../lib/theme.js';
import { LOGO_URL } from '../lib/constants.js';

const EMPTY = { business_name_en: '', business_name_ar: '', contact_name: '', email: '', phone: '', whatsapp: '', address: '', tax_id: '' };

// ─── PUBLIC: VENDOR APPLICATION (/sell) ───────────────────────────────────────
// Business owners apply to sell on the Sedra Home wholesale platform.
// Rows land in vendors with status='pending'; staff review in the Vendors tab.
export default function VendorApply() {
  const [lang, setLang] = useState(() => localStorage.getItem('sedra_lang') || 'ar');
  const ar  = lang === 'ar';
  const dir = ar ? 'rtl' : 'ltr';

  const [form, setForm]           = useState({ ...EMPTY });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess]     = useState(false);
  const [err, setErr]             = useState('');

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true); setErr('');
    try {
      await supaFetch('POST', 'vendors', {
        business_name_en: form.business_name_en.trim(),
        business_name_ar: form.business_name_ar.trim() || null,
        contact_name:     form.contact_name.trim(),
        email:            form.email.trim().toLowerCase(),
        phone:            form.phone.trim(),
        whatsapp:         form.whatsapp.trim() || form.phone.trim(),
        address:          form.address.trim() || null,
        tax_id:           form.tax_id.trim() || null,
        preferred_language: lang,
      }, '', { 'Prefer': 'return=minimal' });
      posthog.capture('vendor_application_submitted', {
        preferred_language: lang,
        has_tax_id: Boolean(form.tax_id.trim()),
        has_address: Boolean(form.address.trim()),
      });
      setSuccess(true);
    } catch (e2) {
      posthog.captureException(e2, { flow: 'vendor_application' });
      setErr(ar ? 'حدث خطأ — حاول مرة أخرى' : 'Something went wrong — please try again');
    }
    setSubmitting(false);
  };

  const fields = [
    { key: 'business_name_en', label: ar ? 'اسم النشاط التجاري (إنجليزي)' : 'Business name (EN)', required: true },
    { key: 'business_name_ar', label: ar ? 'اسم النشاط التجاري (عربي)' : 'Business name (AR)', required: false },
    { key: 'contact_name',     label: ar ? 'اسم المسؤول' : 'Contact name', required: true },
    { key: 'email',            label: ar ? 'البريد الإلكتروني' : 'Email', required: true, type: 'email' },
    { key: 'phone',            label: ar ? 'رقم الهاتف' : 'Phone', required: true, type: 'tel' },
    { key: 'whatsapp',         label: ar ? 'واتساب (اختياري)' : 'WhatsApp (optional)', required: false, type: 'tel' },
    { key: 'address',          label: ar ? 'العنوان (اختياري)' : 'Address (optional)', required: false },
    { key: 'tax_id',           label: ar ? 'السجل التجاري / الرقم الضريبي (اختياري)' : 'Tax ID / Trade license (optional)', required: false },
  ];

  return (
    <div style={{ minHeight: '100vh', background: COLORS.page, fontFamily: FONT, direction: dir, padding: '40px 16px' }}>
      <div style={{ maxWidth: 560, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 26 }}>
          <img src={LOGO_URL} alt="Sedra Home" style={{ width: 84, borderRadius: 10, marginBottom: 12 }} />
          <h1 style={{ margin: '0 0 8px', fontSize: 22, color: COLORS.brandDark }}>
            {ar ? 'بيع منتجاتك على منصة سدرة هوم' : 'Sell your products on Sedra Home'}
          </h1>
          <p style={{ margin: 0, fontSize: 13.5, color: COLORS.textMuted, lineHeight: 1.8 }}>
            {ar
              ? 'انضم لمنصة الجملة الشاملة — منتجاتك أمام مئات تجار التجزئة، تحت علامة "يُباع عبر سدرة هوم".'
              : 'Join the all-in-one wholesale platform — your products in front of hundreds of retailers, branded "Sold via Sedra Home".'}
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
            <LangToggle lang={lang} setLang={setLang} variant="light" size="sm" />
          </div>
        </div>

        {success ? (
          <div style={{ background: '#fff', borderRadius: 14, padding: '38px 30px', textAlign: 'center', boxShadow: '0 8px 40px rgba(74,82,64,0.12)' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10, color: COLORS.success }}><Icon name="PartyPopper" size={44} /></div>
            <div style={{ fontWeight: 800, fontSize: 17, color: COLORS.brandDark, marginBottom: 8 }}>
              {ar ? 'تم استلام طلبك!' : 'Application received!'}
            </div>
            <div style={{ fontSize: 13.5, color: COLORS.textMuted, lineHeight: 1.8 }}>
              {ar
                ? 'سيتواصل معك فريق سدرة هوم خلال ٤٨ ساعة لتفعيل حسابك.'
                : 'The Sedra Home team will contact you within 48 hours to activate your account.'}
            </div>
          </div>
        ) : (
          <form onSubmit={submit} style={{ background: '#fff', borderRadius: 14, padding: '30px 26px', boxShadow: '0 8px 40px rgba(74,82,64,0.12)' }}>
            {fields.map(f => (
              <div key={f.key} style={{ marginBottom: 12 }}>
                <label style={{ display: 'block', fontSize: 12.5, fontWeight: 700, color: COLORS.brandDark, marginBottom: 5 }}>
                  {f.label}{f.required && <span style={{ color: COLORS.danger }}> *</span>}
                </label>
                <input style={input} type={f.type || 'text'} required={f.required} value={form[f.key]}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))} />
              </div>
            ))}
            {err && <div style={{ padding: '9px 14px', background: COLORS.dangerBg, color: COLORS.danger, borderRadius: 8, marginBottom: 12, fontSize: 13 }}>{err}</div>}
            <button type="submit" disabled={submitting}
              style={{ ...button(submitting ? '#aaa' : COLORS.brand), width: '100%', padding: '13px 0', fontSize: 15 }}>
              {submitting ? (ar ? 'جارٍ الإرسال…' : 'Submitting…') : (ar ? 'إرسال الطلب' : 'Submit application')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
